import { supabase } from '../lib/supabase'

export {}

export type StripeNormalizedStatus = 'active' | 'trial' | 'expired' | 'cancelled'

export interface StripeStatusResponse {
  provider: 'stripe'
  status: StripeNormalizedStatus
  subscriptionId?: string
  currentPeriodEnd?: number
  trialEnd?: number
}

export async function createStripeCheckoutSession(origin: string): Promise<{ url: string; sessionId?: string }> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Utilisateur non authentifié')

  const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-create-checkout-session`

  const res = await fetch(fnUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ origin }),
  })

  const json = await res.json().catch(() => null)
  if (!res.ok) {
    // Log utile pour debug (renvoie généralement { error: '...' })
    console.error('[stripeEdgeService] create-checkout-session failed', res.status, json)
    throw new Error((json as any)?.error || 'Erreur création session Stripe')
  }

  return { url: json.url, sessionId: json.sessionId }
}

export async function getStripeStatus(): Promise<StripeStatusResponse> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Utilisateur non authentifié')

  const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-get-status`

  const res = await fetch(fnUrl, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  const json = await res.json()
  if (!res.ok) {
    throw new Error(json?.error || 'Erreur récupération status Stripe')
  }

  return json as StripeStatusResponse
}

export interface CreatePaymentIntentResponse {
  clientSecret: string
  customerId: string
  setupIntentId: string
}

export async function createStripePaymentIntent(): Promise<CreatePaymentIntentResponse> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Utilisateur non authentifié')

  const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-create-payment-intent`

  const res = await fetch(fnUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  const json = await res.json()
  if (!res.ok) {
    console.error('[stripeEdgeService] create-payment-intent failed', res.status, json)
    throw new Error(json?.error || 'Erreur création PaymentIntent Stripe')
  }

  return json as CreatePaymentIntentResponse
}

export interface ConfirmPaymentResponse {
  success: boolean
  subscriptionId: string
  status: string
  trialEnd?: number
  currentPeriodEnd?: number
}

export async function confirmStripePayment(setupIntentId: string): Promise<ConfirmPaymentResponse> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Utilisateur non authentifié')

  const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-confirm-payment`

  const res = await fetch(fnUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ setupIntentId }),
  })

  const json = await res.json()
  if (!res.ok) {
    console.error('[stripeEdgeService] confirm-payment failed', res.status, json)
    throw new Error(json?.error || 'Erreur confirmation paiement Stripe')
  }

  return json as ConfirmPaymentResponse
}

export interface CancelStripeSubscriptionResponse {
  success: boolean
  subscriptionId: string
  status: string
}

export interface CreatePaygPaymentResponse {
  clientSecret: string
  paymentIntentId: string
}

export async function createStripePaygPayment(
  amountCents: number,
  currency: string,
  planLabel: string
): Promise<CreatePaygPaymentResponse> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Utilisateur non authentifié')

  const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-create-payg-payment`

  const res = await fetch(fnUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ amount: amountCents, currency, planLabel }),
  })

  const json = await res.json()
  if (!res.ok) {
    console.error('[stripeEdgeService] create-payg-payment failed', res.status, json)
    throw new Error(json?.error || 'Erreur création paiement PAYG Stripe')
  }

  return json as CreatePaygPaymentResponse
}

export interface CreateSubscriptionFallbackResponse {
  clientSecret: string
  subscriptionId: string
  customerId: string
}

// NB : l'edge function stripe-create-subscription-fallback est partagée avec
// goparkingnow et n'a pas de prix `interval=day`. Le plan journalier n'a donc
// pas de repli Stripe — seul le mensuel passe par ici.
export async function createStripeSubscriptionFallback(
  plan: 'monthly',
  currency: string
): Promise<CreateSubscriptionFallbackResponse> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Utilisateur non authentifié')

  const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-create-subscription-fallback`

  const res = await fetch(fnUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ plan, currency }),
  })

  const json = await res.json()
  if (!res.ok) {
    console.error('[stripeEdgeService] create-subscription-fallback failed', res.status, json)
    throw new Error(json?.error || 'Erreur création abonnement Stripe')
  }

  return json as CreateSubscriptionFallbackResponse
}

export async function cancelStripeSubscription(): Promise<CancelStripeSubscriptionResponse> {
  const { data: session } = await supabase.auth.getSession()
  const token = session.session?.access_token
  if (!token) throw new Error('Utilisateur non authentifié')

  const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/stripe-cancel-subscription`

  const res = await fetch(fnUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  const json = await res.json()
  if (!res.ok) {
    console.error('[stripeEdgeService] cancel-subscription failed', res.status, json)
    throw new Error(json?.error || 'Erreur annulation abonnement Stripe')
  }

  return json as CancelStripeSubscriptionResponse
}
